import { useEffect, useRef, useState } from 'react';
import { Star, Flame } from 'lucide-react';
import { useXp } from '../../context/XpContext';
import TanoCharacter from '../../components/Eleve/TanoCharacter';
import Confetti from '../../components/Eleve/Confetti';
import XpHistorique from '../../components/Eleve/XpHistorique';
import { SKIN_CATEGORIES, SKINS_META, progressionRatio, metriqueLane, UNITE_LANE } from '../../utils/xpConfig';
import { nomPalier } from '../../utils/ileLore';
import { playEquip } from '../../utils/xpSound';

/**
 * Onglet « Moi » — hero jouable (émotions de Tano + niveau/XP), historique des
 * gains, et garde-robe immersive : survol/focus d'un skin débloqué → le hero
 * « essaie » le skin ; équipement célébré (confettis + son + joie). Étiquette
 * « encore X » sur le prochain déblocage de chaque lane (seuils indicatifs, le
 * déblocage reste décidé par le back).
 *
 * Skins verrouillés (décision prof 2026-09-14) : on ne les montre JAMAIS en
 * clair, sinon il n'y a plus rien à découvrir. Trois états :
 *   - le PROCHAIN à portée (`locked-next`) : flou coloré, on devine les
 *     couleurs sans voir le dessin, la mini-barre dit ce qui manque ;
 *   - les suivants (`locked-far`) : silhouette noire et halo de la couleur
 *     dominante (`halo` de SKINS_META), le nom reste, le ruban RARE aussi ;
 *   - le hero au survol d'un verrouillé : silhouette + « Débloque-le pour le
 *     découvrir : encore X », jamais l'aperçu en clair.
 * La révélation se joue au déblocage : la cellule flashe, le hero porte le
 * skin deux secondes (`justUnlocked`), confettis.
 */
const MOODS = [
  { id: 'joy', label: '😄 Joyeux' },
  { id: 'sad', label: '😢 Triste' },
  { id: 'angry', label: '😠 Colère' },
  { id: 'sleep', label: '😴 Dodo' },
  { id: 'idle', label: '😌 Neutre' },
];

function MoiEleve({ prenom }) {
  const { profil, equiperSkin } = useXp();
  const [heroMood, setHeroMood] = useState('joy');
  // Preview au survol/focus : { type, id, unlocked, label } | null
  const [preview, setPreview] = useState(null);
  // Célébration d'équipement (confettis + joie forcée ~1,6 s)
  const [justEquipped, setJustEquipped] = useState(false);
  const equipTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(equipTimerRef.current), []);
  // Révélation d'un skin qui vient d'être débloqué : { type, id } | null.
  // Détectée par comparaison de `skins_debloques` entre deux profils ; au
  // premier profil reçu, rien n'est révélé (tout est « déjà là »).
  const [justUnlocked, setJustUnlocked] = useState(null);
  const debloquesPrecRef = useRef(null);
  const unlockTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(unlockTimerRef.current), []);
  const skinsDebloques = profil?.skins_debloques;
  useEffect(() => {
    if (!skinsDebloques) return;
    const prec = debloquesPrecRef.current;
    debloquesPrecRef.current = skinsDebloques;
    if (!prec) return;
    for (const type of Object.keys(skinsDebloques)) {
      const nouveau = (skinsDebloques[type] || []).find((id) => !(prec[type] || []).includes(id));
      if (nouveau) {
        setJustUnlocked({ type, id: nouveau });
        setHeroMood('joy');
        playEquip();
        clearTimeout(unlockTimerRef.current);
        unlockTimerRef.current = setTimeout(() => setJustUnlocked(null), 2000);
        return;
      }
    }
  }, [skinsDebloques]);

  if (!profil) {
    return <div className="flex-1 flex items-center justify-center p-6 text-sm" style={{ color: 'rgba(255,255,255,.6)' }}>Chargement de ton profil…</div>;
  }

  const equipe = {
    plumage: profil.skin_plumage || '',
    accessoire: profil.skin_accessoire || '',
    aura: profil.skin_aura || '',
  };
  const debloques = profil.skins_debloques || {};
  const ratio = progressionRatio(profil);
  const reste = Math.max(0, (profil.progression?.xp_seuil_suivant ?? profil.xp_total ?? 0) - (profil.xp_total ?? 0));
  const flammeOn = (profil.flamme_jours || 0) > 0;

  // Le hero porte le skin en PREVIEW par-dessus l'équipement réel ; un skin
  // qui vient d'être débloqué prend la main deux secondes (la révélation).
  const porte = justUnlocked || preview;
  const heroSkins = {
    plume: porte?.type === 'plumage' ? porte.id : equipe.plumage,
    acc: porte?.type === 'accessoire' ? porte.id : equipe.accessoire,
    aura: porte?.type === 'aura' ? porte.id : equipe.aura,
  };
  const heroLocked = Boolean(preview && !preview.unlocked && !justUnlocked);
  const heroHalo = heroLocked ? (SKINS_META[preview.type]?.[preview.id]?.halo || '#29B6E8') : undefined;

  // Re-clic sur un skin équipé = déséquiper ('').
  const onClickSkin = (type, id, unlocked) => {
    if (!unlocked) return;
    const estEquipement = equipe[type] !== id;
    equiperSkin(type, estEquipement ? id : '');
    if (estEquipement) {
      playEquip();
      setHeroMood('joy');
      setJustEquipped(true);
      clearTimeout(equipTimerRef.current);
      equipTimerRef.current = setTimeout(() => setJustEquipped(false), 1600);
    }
  };

  return (
    <div className="flex-1 w-full px-4 py-5 max-w-md xl:max-w-3xl mx-auto flex flex-col gap-4">
      {/* Hero immersif : Tano jouable + niveau + XP manquant */}
      <div
        className={`skin-hero relative overflow-hidden${preview || justUnlocked ? ' previewing' : ''}${heroLocked ? ' preview-locked' : ''}`}
        style={heroHalo ? { '--halo': heroHalo } : undefined}
      >
        {(justEquipped || justUnlocked) && <Confetti />}
        <div className="flex justify-center">
          <TanoCharacter
            plume={heroSkins.plume}
            acc={heroSkins.acc}
            aura={heroSkins.aura}
            mood={heroMood}
            reactive={heroMood === 'joy' || heroMood === 'sad'}
            size={150}
          />
        </div>
        <h1 className="text-white text-xl mt-1" style={{ fontFamily: 'Fredoka' }}>{prenom}</h1>

        {/* Bandeau d'aperçu (survol d'un skin) */}
        {justUnlocked && (
          <p className="text-xs font-bold mt-0.5" style={{ color: '#FFB938' }}>
            Débloqué : {SKINS_META[justUnlocked.type]?.[justUnlocked.id]?.label}
          </p>
        )}
        {preview && !justUnlocked && (
          <p className="text-xs font-bold mt-0.5" style={{ color: preview.unlocked ? '#8FD6F5' : 'rgba(255,255,255,.55)' }}>
            {preview.unlocked
              ? `Aperçu : ${preview.label}, clique pour équiper`
              : `Débloque-le pour le découvrir${preview.manque ? ` : ${preview.manque}` : ''}`}
          </p>
        )}

        {/* Niveau + barre de progression */}
        <div className="flex items-center gap-2 mt-2 max-w-xs mx-auto">
          <span
            className="flex items-center justify-center w-8 h-8 rounded-lg font-extrabold text-sm flex-shrink-0 text-white"
            style={{ background: 'linear-gradient(135deg,#29B6E8,#1E3A8A)' }}
          >
            {profil.niveau}
          </span>
          <div className="h-2.5 flex-1 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,.3)' }}>
            <div
              className="h-full rounded-full transition-all duration-700 ease-out"
              style={{ width: `${Math.round(ratio * 100)}%`, background: 'linear-gradient(90deg,#29B6E8,#FFB938)' }}
            />
          </div>
        </div>
        {/* Titre de palier (ascension de l'île — ileLore) */}
        <p className="text-[13px] font-extrabold mt-1.5" style={{ fontFamily: 'Fredoka', color: '#FFB938' }}>
          {nomPalier(profil.niveau).emoji} {nomPalier(profil.niveau).nom}
        </p>
        <p className="text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,.65)' }}>
          {profil.xp_total} XP{reste > 0 ? ` · plus que ${reste} XP pour le niveau ${profil.niveau + 1}` : ''}
        </p>
        <div className="flex items-center justify-center gap-4 mt-1 text-sm font-bold text-white">
          <span className="flex items-center gap-1"><Star className="w-4 h-4" style={{ color: '#FFB938' }} /> {profil.etoiles_total}</span>
          <span className="flex items-center gap-1" style={{ opacity: flammeOn ? 1 : 0.45 }}>
            <Flame className="w-4 h-4" style={{ color: flammeOn ? '#F97316' : '#94a3b8' }} /> {profil.flamme_jours}
          </span>
        </div>

        {/* Boutons d'émotion */}
        <div className="flex flex-wrap gap-2 justify-center mt-3">
          {MOODS.map((m) => (
            <button
              key={m.id}
              type="button"
              className={`skin-mood-btn ${m.id}${heroMood === m.id ? ' on' : ''}`}
              onClick={() => setHeroMood(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Historique des derniers gains d'XP */}
      <XpHistorique />

      {/* Garde-robe par lane (plumage / accessoire / aura) */}
      {SKIN_CATEGORIES.map((cat) => {
        const dispo = debloques[cat.type] || [];
        const meta = SKINS_META[cat.type] || {};
        const ids = Object.keys(meta);
        const metrique = metriqueLane(profil, cat.type);
        // Première cellule verrouillée de la lane → étiquette « encore X » + mini-barre.
        const prochainVerrouille = ids.find(id => !dispo.includes(id) && (meta[id]?.seuil ?? 0) > metrique);
        return (
          <div key={cat.type} className={`skin-lane ${cat.laneClass}`}>
            <div className="skin-lane-h">
              <span className="ic">{cat.icon}</span>
              <h2 className="text-white">{cat.titre}</h2>
              <span className="skin-tag">{cat.tag}</span>
            </div>
            <div className="skin-lane-sub">{cat.sousTitre}</div>
            <div className="skin-row">
              {ids.map((id, i) => {
                const unlocked = dispo.includes(id);
                const equipped = equipe[cat.type] === id;
                const m = meta[id];
                // Aperçu : Tano avec UNIQUEMENT ce skin (selon la catégorie).
                const previewSkin = {
                  plume: cat.type === 'plumage' ? id : undefined,
                  acc: cat.type === 'accessoire' ? id : undefined,
                  aura: cat.type === 'aura' ? id : undefined,
                };
                const estProchain = id === prochainVerrouille;
                const manque = estProchain ? Math.max(0, (m.seuil ?? 0) - metrique) : 0;
                const progressPct = estProchain && (m.seuil ?? 0) > 0
                  ? Math.round(Math.min(1, metrique / m.seuil) * 100)
                  : 0;
                // Ce que le hero dira au survol d'un verrouillé : « encore 80 XP » pour
                // le prochain, le seuil pour les autres.
                const manqueTexte = estProchain && manque > 0 ? `encore ${manque} ${UNITE_LANE[cat.type]}` : m.seuilTexte;
                const apercu = { type: cat.type, id, unlocked, label: m.label, manque: unlocked ? '' : manqueTexte };
                const revele = justUnlocked?.type === cat.type && justUnlocked?.id === id;
                const etatVerrou = unlocked ? '' : estProchain ? ' locked locked-next' : ' locked locked-far';
                return (
                  <button
                    key={id}
                    type="button"
                    // pas `disabled` : React n'envoie aucun événement souris à un bouton
                    // désactivé, le survol d'un verrouillé n'atteignait jamais le hero ;
                    // le clic est déjà ignoré par onClickSkin, le curseur par le CSS
                    aria-disabled={!unlocked}
                    onClick={() => onClickSkin(cat.type, id, unlocked)}
                    onMouseEnter={() => setPreview(apercu)}
                    onMouseLeave={() => setPreview(null)}
                    onFocus={() => setPreview(apercu)}
                    onBlur={() => setPreview(null)}
                    className={`skin-cell tano-cell${m.rare ? ' rare' : ''}${equipped ? ' equipped' : ''}${etatVerrou}${revele ? ' reveal' : ''}`}
                    style={{ '--halo': m.halo || cat.thColor }}
                  >
                    <span className="skin-seq">{i + 1}</span>
                    {!unlocked && <span className="skin-lock">🔒</span>}
                    {m.rare && <span className="skin-rare-ruban">RARE</span>}
                    <div className="flex justify-center">
                      <TanoCharacter {...previewSkin} mood="joy" size={90} />
                    </div>
                    <div className="skin-nm">{m.label}</div>
                    <div className="skin-th" style={{ color: equipped ? '#29B6E8' : cat.thColor }}>
                      {equipped
                        ? 'Équipé'
                        : unlocked
                          ? 'Disponible'
                          : estProchain && manque > 0
                            ? `encore ${manque} ${UNITE_LANE[cat.type]}`
                            : m.seuilTexte}
                    </div>
                    {estProchain && !unlocked && (
                      <div className="skin-progress-mini"><div style={{ width: `${progressPct}%` }} /></div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default MoiEleve;
